import { defineStore } from 'pinia'
import { getFromStorage, saveToStorage } from '@/helpers/storage'

/**
 * Media store — state for the location media library page.
 *
 * Populated by `OrganizationsLocations.media_v2` styxie via `setMediaConfig(props.mediaData)`.
 *
 * @property {Array} categories - Available media categories
 * @property {Array} identityCategories - Identity-specific media categories
 * @property {boolean} apiConnection - Whether the location has an active Google API connection
 * @property {string|null} timeZone - Location's time zone
 * @property {Array} galleryTags - Available gallery tags for filtering
 * @property {string|null} remoteLocationId - Google location remote ID
 * @property {string|null} locationObfuscatedId - Obfuscated location ID
 * @property {boolean} hasAliveAccount - Whether the Google account is alive
 * @property {boolean} editor - Whether current user has edit permissions
 */
export const useMediaStore = defineStore('media', {
  state: () => ({
    // Config from serializer
    categories: [],
    identityCategories: [],
    apiConnection: false,
    timeZone: null,
    galleryTags: [],
    remoteLocationId: null,
    locationObfuscatedId: null,
    hasAliveAccount: false,
    editor: false,

    // UI state
    viewMode: 'grid',
    uploadModalOpen: false,
    editingMedia: null,
    exifPanelOpen: false,
    previewMedia: null

  }),
  getters: {
    canUpload: (state) => state.apiConnection && state.editor && state.hasAliveAccount
  },
  actions: {
    setMediaConfig(data) {
      this.categories = data.categories || []
      this.identityCategories = data.identityCategories || []
      this.apiConnection = data.apiConnection || false
      this.timeZone = data.timeZone || null
      this.galleryTags = data.galleryTags || []
      this.remoteLocationId = data.remoteLocationId || null
      this.locationObfuscatedId = data.locationObfuscatedId || null
      this.hasAliveAccount = data.hasAliveAccount || false
      this.editor = data.editor || false

      const savedViewMode = getFromStorage(this.storageKey())
      if (savedViewMode) this.viewMode = savedViewMode
    },
    setViewMode(mode) {
      this.viewMode = mode
      saveToStorage(this.storageKey(), mode)
    },
    openUploadModal(media = null) {
      this.editingMedia = media
      this.uploadModalOpen = true
    },
    closeUploadModal() {
      this.uploadModalOpen = false
      this.editingMedia = null
    },
    openPreview(media) {
      this.previewMedia = media
    },
    closePreview() {
      this.previewMedia = null
    },
    storageKey() {
      return `media_view_mode_${this.remoteLocationId || 'default'}`
    }
  }
})
